'use client';

import Script from 'next/script';
import { type FC, type PropsWithChildren, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { TELEMETRY_EVENTS } from '@/lib/telemetry/events';
import { trackEvent, trackFrontendError } from '@/lib/telemetry/yandex';

type PublicClientEnv = {
  NEXT_PUBLIC_RECAPTCHA_SITE_KEY?: string;
  NEXT_PUBLIC_APP_ENV?: string;
  RECAPTCHA_DISABLED?: string;
};

function readClientEnv(): PublicClientEnv | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __ENV__?: PublicClientEnv }).__ENV__;
}

function getRecaptchaKey(): string {
  if (typeof window !== 'undefined') {
    return readClientEnv()?.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';
  }
  return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';
}

/** Test deploys: set `NEXT_PUBLIC_APP_ENV=test` — reCAPTCHA stub only, no Google script. */
function isRecaptchaDisabled(): boolean {
  const fromWindow = readClientEnv();
  const appEnv = fromWindow?.NEXT_PUBLIC_APP_ENV ?? process.env.NEXT_PUBLIC_APP_ENV ?? '';
  if (appEnv === 'test') return true;
  if (typeof window !== 'undefined') {
    const val = fromWindow?.RECAPTCHA_DISABLED ?? process.env.NEXT_PUBLIC_RECAPTCHA_DISABLED;
    return val === 'true';
  }
  return process.env.NEXT_PUBLIC_RECAPTCHA_DISABLED === 'true' || process.env.RECAPTCHA_DISABLED === 'true';
}

function RecaptchaLegalAttribution() {
  const t = useTranslations('strings');
  return (
    <p className="mt-4 text-xs text-fest-muted-text">
      {t.rich('common.recaptcha', {
        privacy: (chunks) => (
          <a
            rel="nofollow"
            target="_blank"
            href="https://policies.google.com/privacy"
            className="underline hover:text-fest-gold"
          >
            {chunks}
          </a>
        ),
        terms: (chunks) => (
          <a
            rel="nofollow"
            target="_blank"
            href="https://policies.google.com/terms"
            className="underline hover:text-fest-gold"
          >
            {chunks}
          </a>
        ),
      })}
    </p>
  );
}

export const RecaptchaController: FC<
  PropsWithChildren<{
    Wrapper?: FC<PropsWithChildren>;
    /** Подпись Google под формой; по умолчанию скрыта. */
    showLegalNotice?: boolean;
  }>
> = ({
  children,
  Wrapper = ({ children: c }) => <>{c}</>,
  showLegalNotice = false,
}) => {
  const key = getRecaptchaKey();
  const disabled = isRecaptchaDisabled();

  return (
    <>
      {!disabled && key && (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${key}`}
          strategy="afterInteractive"
        />
      )}
      {children}
      {showLegalNotice ? (
        <Wrapper>
          <RecaptchaLegalAttribution />
        </Wrapper>
      ) : null}
    </>
  );
};

const RECAPTCHA_LOAD_MAX_WAIT_MS = 5000;
const RECAPTCHA_LOAD_POLL_MS = 100;

type GrecaptchaLike = {
  ready?: (cb: () => void) => void;
  execute: (k: string, o: { action: string }) => Promise<string>;
};

function waitForGrecaptcha(): Promise<GrecaptchaLike> {
  return new Promise((resolve, reject) => {
    const grecaptcha = (window as unknown as { grecaptcha?: GrecaptchaLike }).grecaptcha;
    if (grecaptcha) {
      resolve(grecaptcha);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      const g = (window as unknown as { grecaptcha?: GrecaptchaLike }).grecaptcha;
      if (g) {
        clearInterval(interval);
        resolve(g);
      } else if (Date.now() - start > RECAPTCHA_LOAD_MAX_WAIT_MS) {
        clearInterval(interval);
        reject(new Error('reCAPTCHA is taking too long to load. Please refresh the page and try again.'));
      }
    }, RECAPTCHA_LOAD_POLL_MS);
  });
}

function waitForGrecaptchaReady(grecaptcha: GrecaptchaLike): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof grecaptcha.ready !== 'function') {
      resolve();
      return;
    }
    let done = false;
    const timeout = window.setTimeout(() => {
      if (!done) {
        done = true;
        reject(new Error('reCAPTCHA is taking too long to initialize. Please refresh the page and try again.'));
      }
    }, RECAPTCHA_LOAD_MAX_WAIT_MS);

    grecaptcha.ready(() => {
      if (done) return;
      done = true;
      window.clearTimeout(timeout);
      resolve();
    });
  });
}

/**
 * @param action — имя действия для reCAPTCHA v3 (логи в консоли Google); для участников используйте отдельное значение.
 */
/**
 * Выполняет reCAPTCHA v3 (или возвращает заглушку, если ключ не задан / отключено в env).
 * Используйте перед POST на бэкенд из любого клиентского кода.
 */
export async function executeRecaptchaAction(action: string): Promise<string> {
  const key = getRecaptchaKey();
  const disabled = isRecaptchaDisabled();

  if (disabled || !key) {
    return 'recaptcha-disabled';
  }

  try {
    const grecaptcha = await waitForGrecaptcha();
    await waitForGrecaptchaReady(grecaptcha);
    return await grecaptcha.execute(key, { action });
  } catch (err) {
    trackEvent(TELEMETRY_EVENTS.recaptchaFailed, { action });
    trackFrontendError('recaptcha_execute', err, { action });
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      msg.includes('timeout') || msg.includes('network')
        ? 'reCAPTCHA failed. Please check your connection and try again.'
        : 'reCAPTCHA verification failed. Please try again.',
    );
  }
}

export function useRecaptchaCallback(callback?: (token: string) => void) {
  return useCallback(
    async (action: string): Promise<string> => {
      const token = await executeRecaptchaAction(action);
      callback?.(token);
      return token;
    },
    [callback],
  );
}
